import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MessageSquareText, Bug, Lightbulb, ThumbsUp, MoreHorizontal, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "bug",        label: "Bug",        Icon: Bug },
  { id: "idea",       label: "Idea",       Icon: Lightbulb },
  { id: "compliment", label: "Compliment", Icon: ThumbsUp },
  { id: "other",      label: "Other",      Icon: MoreHorizontal },
];

const MAX_ERRORS = 10;

/**
 * Auth-aware floating "Tell us what's broken" widget.
 * - Renders ONLY inside authenticated layouts (dashboard / admin / affiliate)
 * - Patches `console.error` to capture last 10 console errors per session
 * - Submits the current URL, viewport, user role, captured errors + a short message
 */
export default function FeedbackWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("bug");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const errorsRef = useRef([]);

  // Patch console.error once per mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.__connexisErrorCapture) return;
    window.__connexisErrorCapture = true;
    const orig = window.console.error;
    window.console.error = (...args) => {
      try {
        const line = args.map((a) => {
          if (a instanceof Error) return `${a.name}: ${a.message}`;
          if (typeof a === "object") { try { return JSON.stringify(a).slice(0, 200); } catch { return String(a); } }
          return String(a);
        }).join(" ").slice(0, 280);
        errorsRef.current = [line, ...errorsRef.current].slice(0, MAX_ERRORS);
      } catch {}
      orig.apply(window.console, args);
    };
    // Also capture window error events (uncaught exceptions)
    const onErr = (e) => {
      const line = `[uncaught] ${e?.message || ""} @ ${e?.filename || ""}:${e?.lineno || ""}`.slice(0, 280);
      errorsRef.current = [line, ...errorsRef.current].slice(0, MAX_ERRORS);
    };
    window.addEventListener("error", onErr);
    return () => window.removeEventListener("error", onErr);
  }, []);

  if (!user) return null;

  const submit = async (e) => {
    e?.preventDefault();
    if (description.trim().length < 4) {
      toast.error("Please add at least a sentence describing what happened.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/feedback", {
        description: description.trim(),
        category,
        page_url: window.location.href,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        user_agent: navigator.userAgent,
        console_errors: errorsRef.current,
      });
      setDone(true);
      setDescription("");
      setTimeout(() => { setOpen(false); setDone(false); }, 1600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — try again.");
    } finally {
      setBusy(false);
    }
  };

  const ActiveIcon = (CATEGORIES.find((c) => c.id === category) || CATEGORIES[0]).Icon;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="feedback-fab"
        aria-label="Send feedback"
        className="fixed bottom-20 right-5 z-50 group flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white pl-3 pr-4 py-2.5 shadow-float border border-white/10 transition-all hover:shadow-2xl"
      >
        <MessageSquareText className="size-4" />
        <span className="text-xs font-medium hidden sm:inline">Feedback</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[520px]" data-testid="feedback-dialog">
          {!done ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><ActiveIcon className="size-5 text-primary" /> Tell us what's happening</DialogTitle>
                <DialogDescription>
                  This goes straight to our team along with your current URL, viewport, and any recent console errors. No PII captured beyond your account email.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-4 gap-2">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCategory(c.id)}
                      data-testid={`feedback-cat-${c.id}`}
                      className={`group flex flex-col items-center gap-1.5 rounded-lg border px-2 py-3 text-xs font-medium transition-all ${
                        category === c.id
                          ? "border-primary bg-primary/5 text-primary"
                          : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                      }`}
                    >
                      <c.Icon className="size-4" />
                      {c.label}
                    </button>
                  ))}
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="feedback-desc">What happened?</Label>
                  <Textarea
                    id="feedback-desc"
                    data-testid="feedback-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={
                      category === "bug" ? "I clicked X and Y happened instead of Z…" :
                      category === "idea" ? "It would be helpful if…" :
                      category === "compliment" ? "The thing I love most is…" :
                      "What's on your mind?"
                    }
                    rows={4}
                    required
                  />
                </div>

                <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-[11px] text-slate-600 font-mono space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-400">URL</span>
                    <span className="truncate text-right">{typeof window !== "undefined" ? window.location.pathname : ""}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-400">Viewport</span>
                    <span>{typeof window !== "undefined" ? `${window.innerWidth} × ${window.innerHeight}` : ""}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-slate-400">Console errors captured</span>
                    <span className={errorsRef.current.length > 0 ? "text-amber-600 font-semibold" : ""}>{errorsRef.current.length}</span>
                  </div>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
                  <Button type="submit" disabled={busy} data-testid="feedback-submit" className="rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white">
                    {busy ? <><Loader2 className="size-4 animate-spin mr-1.5" /> Sending</> : "Send feedback"}
                  </Button>
                </DialogFooter>
              </form>
            </>
          ) : (
            <div className="py-8 text-center" data-testid="feedback-thanks">
              <CheckCircle2 className="size-12 text-emerald-500 mx-auto" />
              <h3 className="mt-3 text-lg font-semibold text-slate-900">Thanks — we got it.</h3>
              <p className="mt-1 text-sm text-slate-600">Our team reviews every report. You'll get a reply within 1 working day if it's something we need more info on.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
